import { useLazyQuery } from '@apollo/client'
import { Box, FormHelperText } from '@mui/material'
import { UploadedFile } from '@sitoo/mui-components'
import { enqueueSnackbar } from 'notistack'
import { useCallback, useState } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Form } from '../../..'
import { GetResourceDocument } from '../../../../../../generated/graphql'
import { getErrorMessages } from '../../../../../../utils/error-mapping'
import { IMAGE_ACCEPT } from '../../../../../product/fields/images'
import { DropzoneInput } from '../../../../../../inputs/dropzone-input'

export const LogotypeField = () => {
  const { t } = useTranslation(['pos_profiles'])
  const { getValues, setError, setValue, clearErrors, control } =
    useFormContext<Form>()
  const { fieldState } = useController({ name: 'logotype', control })
  const [logotype, setLogotype] = useState(getValues('logotypeResource'))

  const errorMessage = fieldState.error?.message

  const [getResource] = useLazyQuery(GetResourceDocument, {
    fetchPolicy: 'network-only',
  })

  const updateLogotype = useCallback(
    async (file: UploadedFile | null) => {
      const fileName = file?.file?.name?.replace(/[^a-zA-Z0-9_.-]/g, '')

      clearErrors('logotype')

      if (!fileName) {
        setLogotype(undefined)
        return
      }

      if (fileName === logotype) {
        setError('logotype', {
          message: t(
            'pos_profiles:logotype_field.image_name_already_used_in_profile',
          ),
        })
        return
      }

      try {
        const { data } = await getResource({
          variables: { resourceId: fileName },
        })

        const existingResourceId = data?.resource?.resourceid

        if (existingResourceId) {
          setError('logotype', {
            message: t('pos_profiles:logotype_field.existing_image_helper'),
          })
          setValue('logotype', existingResourceId, { shouldDirty: true })
          setValue('logotypeToUpload', undefined, { shouldDirty: true })
        }
      } catch (error) {
        const errorMessage = getErrorMessages(error)[0]
        enqueueSnackbar(errorMessage, { variant: 'error' })
      }
    },
    [clearErrors, getResource, logotype, setError, setValue, t],
  )

  return (
    <Box>
      <DropzoneInput
        name="logotypeToUpload"
        resource={logotype}
        control={control}
        multiple={false}
        label={t('pos_profiles:logotype_field.label')}
        helperText={t('pos_profiles:logotype_field.description')}
        accept={IMAGE_ACCEPT}
        maxSize={7 * 1024 * 1024}
        onChange={updateLogotype}
        sx={{ '.DropzoneContainer': { gridTemplateColumns: 'none' } }}
      />

      <FormHelperText error>{errorMessage}</FormHelperText>
    </Box>
  )
}
